import { selx } from "clsx";
import { jsx } from "panel";
import { ButtonSize } from "common/components/button/constants";
import { SectionEl } from "common/components/section";
import { SelectTheme } from "common/components/select/constants";
import { SelectEl } from "common/components/select";
import { SvgIconEl } from "common/components/svg-icon";
import { ToggleEl } from "common/components/toggle";
import { ToggleTheme } from "common/components/toggle/types";
import { TooltipEl } from "common/components/tooltip";
import { DataGroupSelectorEl } from "common/widgets/data-group-selector";
import { useReportConsistency, useRetentionConsistency } from "common/report/util/feature-sampling";
import { CountUsersOnceState, ReportName } from "common/report/constants";
import { QueryEntrySectionEl } from "../query-entry-section";
import { EntryType, isMetricEntry, RowLabelType } from "../query-entry-section/types";
import { QueryReportHeaderEl } from "../query-report-header";
import { BEHAVIOR_MULTI_ATTRIBUTION_FILTER } from "../query-block/properties-menu/constants";
import { getFunnelStepCount, queryBuilderUtils } from "./util";
import styles from "./index.module.styl";
export function template() {
  const hasNewUI = queryBuilderUtils.isCoreReport() && this.params.trackName !== `cohorts`;
  const entries = this.helpers.getEntries(`entries`);
  const globalSegments = this.helpers.getSegmentEntries();
  const isReportSwitchable = Boolean(this.params.switchableReportName);
  const switchableReportHasQueryOptions = isReportSwitchable && this.params.queryTypeOptions;
  const groupedCountMenu = this.params.trackName === `retention` && useRetentionConsistency() || this.params.trackName === `funnels` && useReportConsistency();
  const stepCount = useReportConsistency() && this.params.reportName === ReportName.Funnels ? getFunnelStepCount(entries) : void 0;
  const FilterOrOperatorInfo = () => /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryFilterOperatorInfo)
  }, "Matching Any", /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryFilterOperatorIcon)
  }, /* @__PURE__ */ jsx(SvgIconEl, {
    attrs: { icon: `info` }
  }), /* @__PURE__ */ jsx(TooltipEl, {
    attrs: { "max-width": `225px` }
  }, 'The "Any" operator is deprecated and cannot be changed for this report')));
  const AttributionControl = (props) => this.helpers.showAttributionSelector() && /* @__PURE__ */ jsx("div", {
    sel: selx(styles.breakdownAttributionWrapper)
  }, !hasNewUI && /* @__PURE__ */ jsx("div", {
    sel: selx(styles.text)
  }, "Attribution type"), /* @__PURE__ */ jsx(SelectEl, {
    attrs: {
      elref: `attributionSelect`,
      "always-show-caret": true,
      disabled: Boolean(props == null ? void 0 : props.disabled),
      sections: [{ items: this.params.breakdownAttribution }],
      "selected-option": this.helpers.getAttributionSelectedOption(),
      size: ButtonSize.Small,
      theme: hasNewUI ? SelectTheme.Tertiary : SelectTheme.Select,
      "menu-width": 200,
      "tab-index": 0,
      "experimental-positioning": true
    },
    on: { change: this.helpers.updateAttributionSelection }
  }));
  const GlobalBreakdowns = ({ segmentEntries }) => {
    return !this.params.hideGroupBy && /* @__PURE__ */ jsx(QueryEntrySectionEl, {
      key: `${this.params.selectedQueryType}-breakdowns`,
      attrs: this.helpers.serializeAttributes(
        Object.assign(this.helpers.getPropertyMenuAttributes(`breakdown`), {
          "can-customize-numeric-buckets": this.params.canCustomizeNumericBuckets,
          "data-group-id": this.params.dataGroupId,
          "disable-profile-history": this.params.disableProfileHistory,
          elref: `breakdownSection`,
          entries: JSON.stringify(segmentEntries),
          "enable-sticky-header": true,
          "event-names": JSON.stringify(this.helpers.getEntryValues(`entries`)),
          "exclude-dimension-properties": this.params.excludeDimensionProperties,
          "include-custom-properties": this.params.includeCustomProperties,
          "inline-cohorts-override": this.params.inlineCohortsOverride,
          "other-group-by-options": this.params.otherGroupByOptions,
          "project-id": this.params.projectId,
          "project-is-free-plan": this.params.projectIsFreePlan,
          "row-draggable": true,
          "row-label-type": this.helpers.getNoneRowLabelType(),
          "row-limit": this.params.groupByLimitation,
          "row-types": JSON.stringify([this.helpers.getSegmentEntryRowType()]),
          "scd-poc-enabled": this.params.slowlyChangingDimensionsPocEnabled,
          "show-special-dataset-property": this.params.showSpecialDatasetProperty,
          "step-count": stepCount,
          "support-b2b-cohorts": this.params.supportB2bCohorts,
          "track-properties": { ...this.params.trackProperties, sectionName: `global-breakdown` },
          "undraggable-rows": this.params.groupByUndraggableRows,
          "user-id": this.params.userId,
          "workspace-id": this.params.workspaceId,
          disabled: this.params.disableBreakdown,
          "section-title": `Breakdown`
        })
      ),
      on: {
        savedCustomEvent: this.helpers.onSavedCustomEvent,
        track: this.helpers.captureTracking,
        update: this.helpers.updateSegmentEntrySection
      }
    }, hasNewUI && /* @__PURE__ */ jsx("div", {
      attrs: { slot: `header-items` }
    }, /* @__PURE__ */ jsx(AttributionControl, {
      disabled: this.params.disableBreakdown
    })));
  };
  const GlobalFilters = ({ filterEntries }) => {
    const menuAttrs = this.helpers.getPropertyMenuAttributes(`filter`);
    return /* @__PURE__ */ jsx(QueryEntrySectionEl, {
      key: `${this.params.selectedQueryType}-filters`,
      attrs: this.helpers.serializeAttributes(
        Object.assign(menuAttrs, {
          elref: `filterSection`,
          "data-group-id": this.params.dataGroupId,
          "data-groups-enabled": menuAttrs[`data-groups-enabled`] && !this.params.filterHideDataGroups,
          "disable-profile-history": this.params.disableProfileHistory,
          "enable-enhanced-list-filters": this.params.enableEnhancedListFilters,
          "enable-sticky-header": true,
          "event-names": JSON.stringify(this.helpers.getEntryValues(`entries`)),
          entries: JSON.stringify(filterEntries),
          "exclude-dimension-properties": this.params.excludeDimensionProperties,
          "inline-cohorts-override": this.params.inlineCohortsOverride,
          "hide-events": menuAttrs[`hide-events`],
          "other-filter-options": this.params.otherFilterOptions,
          "project-has-people": menuAttrs[`project-has-people`] && !this.params.filterHideUsers,
          "project-id": this.params.projectId,
          "row-label-type": this.helpers.getNoneRowLabelType(),
          "row-draggable": true,
          "row-types": JSON.stringify([this.helpers.getFilterEntryRowType()]),
          "scd-poc-enabled": this.params.slowlyChangingDimensionsPocEnabled,
          "show-special-dataset-property": this.params.showSpecialDatasetProperty,
          "step-count": stepCount,
          "support-b2b-cohorts": this.params.supportB2bCohorts,
          "track-properties": { ...this.params.trackProperties, sectionName: `global-filter` },
          "user-id": this.params.userId,
          "workspace-id": this.params.workspaceId,
          "section-title": `Filter`
        })
      ),
      on: {
        savedCustomEvent: this.helpers.onSavedCustomEvent,
        track: this.helpers.captureTracking,
        update: this.helpers.updateFilterEntrySection
      }
    }, hasNewUI && /* @__PURE__ */ jsx("div", {
      attrs: { slot: `header-items` }
    }, this.helpers.showFilterOrOperatorInfo() && /* @__PURE__ */ jsx(FilterOrOperatorInfo, null), this.params.enableFilterAttribution && /* @__PURE__ */ jsx(AttributionControl, null)));
  };
  const ExclusionSteps = () => this.params.showExclusionSteps && /* @__PURE__ */ jsx(SectionEl, {
    sel: selx(styles.builderSection),
    attrs: {
      "header-title": !hasNewUI && `Exclusion Steps`,
      "is-closed": !hasNewUI && this.helpers.getEntries(`exclusion-steps`).length === 0,
      "non-collapsible": hasNewUI
    },
    on: { track: this.helpers.captureTracking }
  }, /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryBody)
  }, /* @__PURE__ */ jsx(QueryEntrySectionEl, {
    attrs: {
      "add-entry-label": this.params.addExclusionEventLabel,
      "add-entry-button-tooltip-text": this.params.exclusionStepAddEntryTooltipText,
      "data-group-id": this.params.dataGroupId,
      "data-groups-enabled": this.params.dataGroupsEnabled,
      "disable-first-time-event-filters": this.params.disableFirstTimeEventFilters,
      "disable-profile-history": this.params.disableProfileHistory,
      elref: `exclusionSection`,
      entries: this.helpers.getEntries(`exclusion-steps`),
      "row-draggable": true,
      "entry-type": EntryType.ExclusionEvent,
      "exclude-autotrack-events": this.params.excludeAutotrackEvents,
      "include-custom-properties": this.params.includeCustomProperties,
      "inline-cohorts-override": this.params.inlineCohortsOverride,
      "is-blocked-people": Boolean(this.params.projectHasPeople),
      "num-exclusion-steps": this.helpers.getNumExclusionSteps(),
      "project-has-people": this.params.projectHasPeople,
      "project-has-cohorts": this.params.projectHasCohorts,
      "project-id": this.params.projectId,
      "track-name": this.params.trackName,
      "track-properties": { ...this.params.trackProperties, sectionName: `exclusion-steps` },
      "workspace-id": this.params.workspaceId,
      "project-as-of-date": this.params.projectAsOfDate,
      "section-title": `Exclusion Steps`,
      "row-label-type": hasNewUI ? RowLabelType.None : this.params.entryRowLabelType
    },
    on: {
      savedCustomEvent: this.helpers.onSavedCustomEvent,
      track: this.helpers.captureTracking,
      update: (ev) => this.helpers.updateEntrySection(ev, `exclusion-steps`)
    }
  })));
  const EntriesSection = () => /* @__PURE__ */ jsx(QueryEntrySectionEl, {
    key: `${this.params.selectedQueryType}-entries`,
    attrs: this.helpers.serializeAttributes(
      Object.assign(
        {
          elref: `entrySection`,
          autofocus: !entries.length || !entries.every((entry) => {
            if (isMetricEntry(entry))
              return entry.behavior.name;
            return `value` in entry.event && entry.event.value;
          }),
          "add-entry-label": this.params.addEntryLabel,
          "add-entry-button-label": this.params.addEntryButtonLabel,
          "add-entry-button-tooltip-text": this.params.addEntryButtonTooltipText,
          "add-entry-menu-width": 130,
          "allow-cohort-filter": this.params.allowCohortFilter,
          "behavior-mode": this.params.behaviorMode,
          "can-customize-numeric-buckets": this.params.canCustomizeNumericBuckets,
          "can-use-multi-attribution": this.params.canUseMultiAttribution,
          "count-users-once-state": this.params.groupBy.length > 0 ? CountUsersOnceState.Enabled : CountUsersOnceState.Disabled,
          "can-rename-step": this.params.showEntryRename,
          "chart-type": this.params.chartType,
          "disable-profile-history": this.params.disableProfileHistory,
          "plot-style": this.params.plotStyle,
          "enable-enhanced-list-filters": this.params.enableEnhancedListFilters,
          "enable-hide-entry": this.params.enableHideEntry,
          "enable-sticky-header": this.helpers.reportShouldHaveStickyHeader(),
          "group-event-behavior": this.params.canGroupEvents ? `nest` : `none`,
          "data-group-id": this.params.dataGroupId,
          "data-groups-enabled": this.params.dataGroupsEnabled,
          "date-range": this.params.dateRange,
          "date-unit": this.params.dateUnit,
          "disable-add-event-filters": this.params.disableAddEventFilters,
          "disable-add-entry": this.params.disableAddEntry,
          "disable-add-entry-reason": this.params.disableAddEntryReason,
          "disable-add-grouped-event-reason": this.params.disableAddGroupedEventReason,
          "disable-clone-entry": this.params.disableCloneEntry,
          "disable-delete-entry": this.params.disableDeleteEntry,
          "disable-first-time-event-filters": this.params.disableFirstTimeEventFilters,
          entries: JSON.stringify(entries),
          "min-entries": this.params.minEntries,
          "empty-placeholder": JSON.stringify(this.params.emptyPlaceholder),
          "exclude-autotrack-events": this.params.excludeAutotrackEvents,
          "exclude-custom-events": this.params.excludeCustomEvents,
          "exclude-dimension-properties": this.params.excludeDimensionProperties,
          "include-custom-properties": this.params.includeCustomProperties,
          "inline-cohorts-override": this.params.inlineCohortsOverride,
          "is-blocked-people": !this.params.projectHasPeople,
          "max-data-history-days": this.params.maxDataHistoryDays,
          "metrics-mode": this.params.metricsMode,
          "other-filter-options": this.params.otherFilterOptions.filter(
            (filter) => filter.value !== BEHAVIOR_MULTI_ATTRIBUTION_FILTER.value
          ),
          "project-has-people": this.params.projectHasPeople,
          "project-is-free-plan": this.params.projectIsFreePlan,
          "hide-edit-controls": this.params.hideEditControls,
          "hide-events": true,
          "project-has-cohorts": this.params.projectHasCohorts,
          "project-id": this.params.projectId,
          readonly: this.params.readonlyEntries,
          "restrict-top-events-only": this.params.restrictTopEventsOnly,
          "row-draggable": this.params.entriesDraggable,
          "row-index-toggleable": this.params.entryIndexToggleable,
          "row-index-toggle-icon": this.params.entryIndexToggleIcon,
          "row-label-type": this.params.entryRowLabelType,
          "row-limit": this.params.rowLimit,
          "row-types": this.params.rowTypes,
          "show-any-event": this.params.showAnyEvent,
          "show-all-events": this.params.showAllEvents,
          "show-sessions": this.params.showSessions,
          "show-special-dataset-property": this.params.showSpecialDatasetProperty,
          "support-b2b-cohorts": this.params.supportB2bCohorts,
          "time-display-context": this.params.timeDisplayContext,
          "track-name": this.params.trackName,
          "report-name": this.params.reportName,
          "track-properties": {
            sectionName: `entries`,
            queryType: this.params.selectedQueryType,
            ...this.params.trackProperties
          },
          "user-id": this.params.userId,
          "workspace-id": this.params.workspaceId,
          "project-as-of-date": this.params.projectAsOfDate,
          "section-title": useReportConsistency() && this.params.reportName === ReportName.Funnels ? `Metric` : this.helpers.hasConsolidateMetricsUI() && this.params.reportName === ReportName.Insights ? `Metrics` : switchableReportHasQueryOptions ? void 0 : this.params.headerTitle,
          "row-drop-disabled": this.params.reportName === ReportName.Funnels && useReportConsistency()
        },
        this.helpers.getMetricsModeAttributes()
      )
    ),
    on: {
      savedCustomEvent: this.helpers.onSavedCustomEvent,
      track: this.helpers.captureTracking,
      update: (ev) => this.helpers.updateEntrySection(ev, `entries`)
    }
  }, switchableReportHasQueryOptions && (!this.helpers.hasConsolidateMetricsUI() || this.params.reportName === ReportName.Retention) && /* @__PURE__ */ jsx(SelectEl, {
    sel: selx(styles.mpQueryTypeDropSelection),
    attrs: {
      elref: `queryTypeSelector`,
      slot: `header-items`,
      theme: SelectTheme.Tertiary,
      size: ButtonSize.Medium,
      "menu-width": null,
      "selected-option": this.helpers.getSelectedQueryType(),
      "always-show-caret": true,
      sections: this.helpers.getQueryTypeOptions(),
      "experimental-positioning": true
    },
    on: { change: this.helpers.onQueryTypeSelected },
    class: { [styles.isEmpty]: entries.length === 0 }
  }), this.params.footerItems && /* @__PURE__ */ jsx("div", {
    sel: selx(styles.slot),
    attrs: { slot: `footer-items` }
  }, this.params.footerItems));
  return /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryBuilder),
    class: {
      [styles.contained]: this.params.contained,
      [styles.forNewUi]: hasNewUI,
      [styles.boardsMode]: this.params.reportHeaderParams && this.helpers.isReportSupportedOnDashboard(),
      [styles.multiAttribution]: this.params.canUseMultiAttribution
    }
  }, this.params.dataGroupsEnabled && !this.params.hideDataGroupsSelector && !this.params.reportHeaderOnly && /* @__PURE__ */ jsx(DataGroupSelectorEl, {
    attrs: {
      "project-id": this.params.projectId,
      "selected-data-group-id": this.params.dataGroupId,
      "select-warnings": JSON.stringify(this.helpers.getDataGroupSelectWarnings()),
      theme: `builder`,
      "workspace-id": this.params.workspaceId
    },
    on: { selectDataGroup: this.helpers.onDataGroupSelected }
  }), this.params.reportHeaderParams && /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryReportHeader),
    class: {
      [styles.boardsMode]: this.helpers.isReportSupportedOnDashboard(),
      [styles.isSticky]: this.helpers.useStickyHeader()
    },
    hook: { insert: this.helpers.handleReportHeaderInsert }
  }, /* @__PURE__ */ jsx(QueryReportHeaderEl, {
    params: this.params.reportHeaderParams,
    on: {
      actionclick: this.helpers.actionClick,
      "back-to-board": this.helpers.propagateEvent,
      bookmarkselectoropen: this.helpers.propagateEvent,
      bookmarkselect: this.helpers.propagateEvent,
      editreport: this.helpers.propagateEvent,
      save: this.helpers.propagateEvent,
      update: this.helpers.propagateEvent,
      reset: this.helpers.propagateEvent
    }
  })), !this.params.reportHeaderOnly && /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryBuilderPanel, styles.forBody),
    class: { [styles.sectionContentOnly]: !this.params.headerTitle && this.params.mainEntryNonCollapsible }
  }, isReportSwitchable && /* @__PURE__ */ jsx(ToggleEl, {
    attrs: {
      elref: `reportTypeSelector`,
      theme: ToggleTheme.TabBarHeavy,
      "icon-only-width-threshold": 430,
      selected: this.params.switchableReportName,
      options: this.helpers.getSwitchableReports()
    },
    on: {
      select: this.helpers.onReportTypeSelected,
      hovered: this.helpers.handleQueryTypeHovered
    }
  }), useReportConsistency() && this.params.reportName === ReportName.Retention && /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryHeaderSection)
  }, /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryHeaderSectionLabel)
  }, "Metric")), /* @__PURE__ */ jsx("div", {
    class: {
      [styles.builderGroup]: groupedCountMenu,
      [styles.isReportConsistencyMode]: useReportConsistency()
    }
  }, /* @__PURE__ */ jsx(SectionEl, {
    sel: selx(styles.builderSection),
    attrs: {
      "header-title": hasNewUI || switchableReportHasQueryOptions ? void 0 : this.params.headerTitle,
      "header-title-lowercase": this.params.headerTitleLowercase,
      "non-collapsible": hasNewUI || this.params.mainEntryNonCollapsible
    },
    on: { track: this.helpers.captureTracking }
  }, /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryBody)
  }, /* @__PURE__ */ jsx(EntriesSection, null))), /* @__PURE__ */ jsx(ExclusionSteps, null), this.params.bodySection && /* @__PURE__ */ jsx("div", {
    sel: selx(styles.slot, styles.mpQueryBodySections),
    on: { track: this.helpers.captureTracking }
  }, this.params.bodySection), groupedCountMenu && this.params.countMenu && /* @__PURE__ */ jsx("div", {
    sel: selx(styles.countMenu)
  }, this.params.countMenu)), !this.params.hideFilter && /* @__PURE__ */ jsx(SectionEl, {
    sel: selx(styles.builderSection),
    attrs: {
      elref: `filterSectionDropdown`,
      "header-title": !hasNewUI && `Filters`,
      "is-closed": !this.helpers.getFilterEntries().length && !hasNewUI,
      "non-collapsible": hasNewUI
    },
    on: { track: this.helpers.captureTracking }
  }, /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryBody)
  }, this.helpers.showFilterOrOperatorInfo() && /* @__PURE__ */ jsx(FilterOrOperatorInfo, null), /* @__PURE__ */ jsx(GlobalFilters, {
    filterEntries: this.helpers.getFilterEntries()
  }), this.params.enableFilterAttribution && !hasNewUI && /* @__PURE__ */ jsx(AttributionControl, null))), !this.params.hideGroupBy && /* @__PURE__ */ jsx(SectionEl, {
    sel: selx(styles.builderSection),
    attrs: {
      elref: `breakdownSectionDropdown`,
      "header-title": !hasNewUI && `Breakdowns`,
      "is-closed": !globalSegments.length && !hasNewUI,
      "non-collapsible": hasNewUI
    },
    on: { track: this.helpers.captureTracking }
  }, /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryBody)
  }, /* @__PURE__ */ jsx(GlobalBreakdowns, {
    segmentEntries: globalSegments
  }), !hasNewUI && /* @__PURE__ */ jsx(AttributionControl, {
    disabled: this.params.disableBreakdown
  })))));
}
